import { useContext, useEffect, useMemo, useRef, type MouseEvent } from 'react';
import { toast } from '@/components/ui/sonner';
import { focusField } from '@/utils/focus';
import { ValidationContext } from './ValidationContext';

/**
 * Subscribes to ValidationProvider and emits persistent toast notifications
 * for active errors when config.showToasts is true.
 *
 * Call once per provider context (in the parent component), not per field.
 * Safe to call outside a ValidationProvider — no-ops gracefully.
 */
export function useValidationToasts() {
  const ctx = useContext(ValidationContext);
  // Map of field → { toastId, message } for active error toasts
  const toastState = useRef<Record<string, { id: string | number; message: string }>>({});

  const getActiveErrors = ctx?.getActiveErrors;
  const errors = useMemo(() => getActiveErrors ? getActiveErrors() : [], [getActiveErrors]);
  const showToasts = ctx?.config.showToasts ?? false;

  useEffect(() => {
    if (!showToasts) return;

    const currentFields = new Set(errors.map((e) => e.field));

    // Dismiss toasts for fields that no longer have errors
    for (const field of Object.keys(toastState.current)) {
      if (!currentFields.has(field)) {
        toast.dismiss(toastState.current[field].id);
        delete toastState.current[field];
      }
    }

    // Raise or update toasts for errors
    for (const error of errors) {
      const existing = toastState.current[error.field];
      // Dismiss stale toast if message changed
      if (existing && existing.message !== error.message) {
        toast.dismiss(existing.id);
        delete toastState.current[error.field];
      }
      if (!toastState.current[error.field]) {
        const field = error.field;
        toastState.current[field] = { message: error.message, id: toast.error(error.message, {
          duration: Infinity,
          action: {
            label: 'Fix',
            onClick: (event: MouseEvent<HTMLButtonElement>) => {
              event.preventDefault();
              focusField(field);
            },
          },
        }) };
      }
    }
  }, [errors, showToasts]);

  // Dismiss all toasts on unmount
  useEffect(() => {
    return () => {
      for (const entry of Object.values(toastState.current)) {
        toast.dismiss(entry.id);
      }
      toastState.current = {};
    };
  }, []);
}
